import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { MapPin, SignIn, UserPlus, Eye, EyeSlash, CheckCircle, Warning, CircleNotch } from '@phosphor-icons/react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutShippingAddress = ({ shippingAddress, handleInputChange, onContinue }) => {
  const { user, login } = useAuth();
  const [emailStatus, setEmailStatus] = useState(null);
  const [existingName, setExistingName] = useState('');
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [loadingCity, setLoadingCity] = useState(false);
  const [citySearchResults, setCitySearchResults] = useState([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const skipCityLookupRef = useRef(false);
  const skipPostalLookupRef = useRef(false);
  const cityValueRef = useRef('');
  cityValueRef.current = shippingAddress.city;

  const isLoggedIn = !!user;

  // Lookup city from postal code (only re-runs when postal code changes)
  useEffect(() => {
    const postal = shippingAddress.postal_code;
    if (!postal || postal.length < 5) { setCitySuggestions([]); return; }
    if (skipCityLookupRef.current) { skipCityLookupRef.current = false; return; }
    const t = setTimeout(async () => {
      setLoadingCity(true);
      try {
        const res = await fetch(`https://geo.api.gouv.fr/communes?codePostal=${postal}&fields=nom,codesPostaux&format=json`);
        const data = await res.json();
        const cities = (data || []).map(c => c.nom);
        setCitySuggestions(cities);
        if (cities.length === 1 && cityValueRef.current !== cities[0]) {
          skipPostalLookupRef.current = true;
          handleInputChange({ target: { name: 'city', value: cities[0] } });
        }
      } catch { setCitySuggestions([]); }
      finally { setLoadingCity(false); }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddress.postal_code]);

  // Lookup postal code from city name (only re-runs when city changes)
  useEffect(() => {
    const cityName = shippingAddress.city;
    if (skipPostalLookupRef.current) { skipPostalLookupRef.current = false; return; }
    if (!cityName || cityName.length < 2) { setCitySearchResults([]); setShowCityDropdown(false); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(cityName)}&fields=nom,codesPostaux&limit=8&boost=population&format=json`);
        const data = await res.json();
        const results = (data || []).map(c => ({ name: c.nom, postalCode: c.codesPostaux?.[0] || '' }))
          .sort((a, b) => {
            const aExact = a.name.toLowerCase() === cityName.toLowerCase() ? 0 : 1;
            const bExact = b.name.toLowerCase() === cityName.toLowerCase() ? 0 : 1;
            return aExact - bExact;
          });
        setCitySearchResults(results);
        setShowCityDropdown(results.length > 0 && !(results.length === 1 && results[0].name.toLowerCase() === cityName.toLowerCase()));
      } catch { setCitySearchResults([]); setShowCityDropdown(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [shippingAddress.city]);

  const selectCity = (result) => {
    skipCityLookupRef.current = true;
    skipPostalLookupRef.current = true;
    handleInputChange({ target: { name: 'city', value: result.name } });
    if (result.postalCode) {
      handleInputChange({ target: { name: 'postal_code', value: result.postalCode } });
    }
    setShowCityDropdown(false);
    setCitySearchResults([]);
  };

  // Check email when it changes (debounced)
  const checkEmail = useCallback(async (email) => {
    if (!email || !email.includes('@') || email.length < 5) {
      setEmailStatus(null);
      return;
    }
    // Don't check if already logged in
    if (isLoggedIn) return;

    setEmailStatus('checking');
    try {
      const res = await axios.post(`${API_URL}/api/auth/check-email`, { email });
      if (res.data.exists) {
        setEmailStatus('exists');
        setExistingName(res.data.name || '');
      } else {
        setEmailStatus('new');
      }
    } catch {
      setEmailStatus(null);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkEmail(shippingAddress.email);
    }, 600);
    return () => clearTimeout(timer);
  }, [shippingAddress.email, checkEmail]);

  // Pre-fill address from user profile when logged in
  useEffect(() => {
    if (user && user.email && !shippingAddress.email) {
      handleInputChange({ target: { name: 'email', value: user.email } });
      if (user.name) {
        const parts = user.name.split(' ');
        if (parts.length >= 2) {
          handleInputChange({ target: { name: 'first_name', value: parts[0] } });
          handleInputChange({ target: { name: 'last_name', value: parts.slice(1).join(' ') } });
        } else {
          handleInputChange({ target: { name: 'first_name', value: user.name } });
        }
      }
    }
  }, [user]);

  const handleLogin = async () => {
    setLoginLoading(true);
    setLoginError('');
    const result = await login(shippingAddress.email, loginPassword);
    if (result.success) {
      setShowLoginForm(false);
      setEmailStatus(null);
      setLoginPassword('');
    } else {
      setLoginError(result.error || 'Identifiants incorrects');
    }
    setLoginLoading(false);
  };

  const handleContinue = async () => {
    // If not logged in and new email -> create account
    if (!isLoggedIn && emailStatus === 'new') {
      if (!newPassword || newPassword.length < 6) {
        setRegisterError('Le mot de passe doit contenir au moins 6 caractères');
        return;
      }
      if (newPassword !== confirmPassword) {
        setRegisterError('Les mots de passe ne correspondent pas');
        return;
      }
      setRegisterError('');
      try {
        const name = `${shippingAddress.first_name} ${shippingAddress.last_name}`.trim();
        const res = await axios.post(`${API_URL}/api/auth/register`, {
          email: shippingAddress.email,
          password: newPassword,
          name: name || 'Client',
          role: 'customer'
        });
        // Store tokens
        if (res.data.access_token) {
          localStorage.setItem('access_token', res.data.access_token);
          localStorage.setItem('refresh_token', res.data.refresh_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
        }
      } catch (err) {
        const d = err.response?.data?.detail;
        const msg = Array.isArray(d) ? d.map(e => e?.msg || '').filter(Boolean).join(', ')
          : (typeof d === 'string' ? d : '');
        setRegisterError(msg || 'Erreur lors de la création du compte');
        return;
      }
    }
    onContinue();
  };

  const isValid = shippingAddress.first_name && shippingAddress.email && shippingAddress.address && shippingAddress.postal_code;
  const canContinue = isValid && (isLoggedIn || emailStatus === 'exists' ? false : emailStatus === 'new' ? newPassword.length >= 6 && newPassword === confirmPassword : true);
  // If logged in, just need valid fields
  const finalCanContinue = isLoggedIn ? isValid : (emailStatus === 'exists' ? false : canContinue);

  return (
    <Card className="dashboard-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin size={20} className="icon-primary" />
          Adresse de livraison
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Logged-in banner */}
        {isLoggedIn && (
          <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="logged-in-banner">
            <CheckCircle size={18} className="text-emerald-600" weight="fill" />
            <span className="text-sm text-emerald-800 font-medium">Connecté en tant que {user.name || user.email}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Prénom *</Label>
            <Input name="first_name" value={shippingAddress.first_name} onChange={handleInputChange} required data-testid="shipping-firstname" />
          </div>
          <div className="space-y-2">
            <Label>Nom *</Label>
            <Input name="last_name" value={shippingAddress.last_name} onChange={handleInputChange} required data-testid="shipping-lastname" />
          </div>
        </div>

        {/* Email + account status */}
        <div className="space-y-2">
          <Label>Email *</Label>
          <Input name="email" type="email" value={shippingAddress.email} onChange={handleInputChange} required
            disabled={isLoggedIn}
            data-testid="shipping-email" />

          {/* Account exists -> login prompt */}
          {!isLoggedIn && emailStatus === 'exists' && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="email-exists-banner">
              <div className="flex items-center gap-2 mb-2">
                <SignIn size={16} className="text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">
                  Un compte existe avec cet email{existingName ? ` (${existingName})` : ''}
                </span>
              </div>
              {!showLoginForm ? (
                <Button variant="outline" size="sm" onClick={() => setShowLoginForm(true)}
                  className="text-sm border-amber-300 text-amber-700 hover:bg-amber-100"
                  data-testid="show-login-btn">
                  <SignIn size={14} className="mr-1" /> Se connecter
                </Button>
              ) : (
                <div className="space-y-2 mt-2">
                  <Input type="password" placeholder="Mot de passe" value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleLogin()}
                    data-testid="login-password-input" />
                  {loginError && <p className="text-xs text-red-600">{loginError}</p>}
                  <a href="/forgot-password" className="text-[11px] text-slate-400 hover:text-slate-600 underline block" data-testid="checkout-forgot-password-link">Mot de passe oublié ?</a>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleLogin} disabled={loginLoading || !loginPassword}
                      className="btn-primary text-sm" data-testid="login-submit-btn">
                      {loginLoading ? 'Connexion...' : 'Se connecter'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setShowLoginForm(false); setLoginError(''); }}
                      className="text-sm">Annuler</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* New email -> create account */}
          {!isLoggedIn && emailStatus === 'new' && shippingAddress.first_name && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="create-account-banner">
              <div className="flex items-center gap-2 mb-3">
                <UserPlus size={16} className="text-blue-600" />
                <span className="text-sm font-semibold text-blue-800">Créer votre compte pour suivre vos commandes</span>
              </div>
              <div className="space-y-2">
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} placeholder="Choisir un mot de passe (min. 6 caractères)"
                    value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    data-testid="register-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <Input type={showPassword ? 'text' : 'password'} placeholder="Confirmer le mot de passe"
                  value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  data-testid="register-confirm-input" />
                {registerError && <p className="text-xs text-red-600">{registerError}</p>}
                {newPassword && confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                )}
                {newPassword && newPassword.length >= 6 && newPassword === confirmPassword && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Mot de passe valide</p>
                )}
              </div>
            </div>
          )}

          {emailStatus === 'checking' && (
            <p className="text-xs text-slate-400">Vérification de l'email...</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Téléphone *</Label>
            <Input name="phone" value={shippingAddress.phone} onChange={handleInputChange} required data-testid="shipping-phone" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Adresse *</Label>
          <Input name="address" value={shippingAddress.address} onChange={handleInputChange} required data-testid="shipping-address" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Code postal *</Label>
            <Input name="postal_code" value={shippingAddress.postal_code}
              onChange={handleInputChange}
              placeholder="13800" required data-testid="shipping-postal" />
          </div>
          <div className="space-y-2 relative">
            <Label>Ville *</Label>
            {loadingCity ? (
              <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-slate-50 text-sm text-slate-400">
                <CircleNotch size={14} className="animate-spin" /> Recherche...
              </div>
            ) : citySuggestions.length > 1 ? (
              <select name="city" value={shippingAddress.city}
                onChange={e => { skipPostalLookupRef.current = true; handleInputChange({ target: { name: 'city', value: e.target.value } }); }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="shipping-city">
                <option value="">Sélectionner la ville</option>
                {citySuggestions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : (
              <>
                <Input name="city" value={shippingAddress.city}
                  onChange={handleInputChange}
                  onFocus={() => { if (citySearchResults.length > 0) setShowCityDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowCityDropdown(false), 200)}
                  placeholder="Istres, Martigues..." required data-testid="shipping-city"
                  autoComplete="off" />
                {showCityDropdown && citySearchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-[200px] overflow-y-auto" data-testid="city-suggestions">
                    {citySearchResults.map((r, i) => (
                      <button key={i} type="button"
                        onMouseDown={e => { e.preventDefault(); selectCity(r); }}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50 flex items-center justify-between border-b border-slate-50 last:border-0">
                        <span className="font-medium text-slate-800">{r.name}</span>
                        <span className="text-xs text-slate-400">{r.postalCode}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <Button onClick={handleContinue} className="w-full btn-primary" disabled={!finalCanContinue} data-testid="continue-to-shipping">
          Continuer
        </Button>
      </CardContent>
    </Card>
  );
};

export default CheckoutShippingAddress;
